import type { ChartPoint, DailyMetric, DashboardSummary } from "../types";

type UnknownRow = Record<string, unknown>;

const asNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return 0;
  const normalized = value.replace(/[^0-9,.-]/g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const first = (row: UnknownRow, names: string[]): unknown => {
  for (const name of names) {
    if (row[name] !== undefined && row[name] !== null) return row[name];
  }
  return undefined;
};

export const normalizeDailyMetric = (row: UnknownRow): DailyMetric => {
  const hoursValue = first(row, ["hours", "hours_played", "playing_hours"]);

  return {
    date: String(first(row, ["metric_date", "date", "played_date"]) ?? ""),
    tournaments: asNumber(
      first(row, ["tournaments", "tournament_count", "games", "games_played"]),
    ),
    entries: asNumber(first(row, ["entries", "entry_count", "total_entries"])),
    investmentUsd: asNumber(
      first(row, ["investment_usd", "buyins_usd", "total_buyin_usd", "investment"]),
    ),
    profitUsd: asNumber(first(row, ["profit_usd", "profit", "net_profit_usd"])),
    cashes: asNumber(first(row, ["cashes", "itm_count", "prizes_count"])),
    hours: hoursValue === undefined ? null : asNumber(hoursValue),
  };
};

const shortDate = (date: string): string => {
  const [year, month, day] = date.split("-");
  return `${day}/${month}/${year.slice(2)}`;
};

export const aggregateMetrics = (rows: DailyMetric[]): DashboardSummary => {
  const byDate = new Map<string, DailyMetric>();

  for (const row of rows) {
    const current = byDate.get(row.date) ?? {
      date: row.date,
      tournaments: 0,
      entries: 0,
      investmentUsd: 0,
      profitUsd: 0,
      cashes: 0,
      hours: null,
    };

    current.tournaments += row.tournaments;
    current.entries += row.entries;
    current.investmentUsd += row.investmentUsd;
    current.profitUsd += row.profitUsd;
    current.cashes += row.cashes;
    if (row.hours !== null) current.hours = (current.hours ?? 0) + row.hours;
    byDate.set(row.date, current);
  }

  const ordered = [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
  let profit = 0;
  let investment = 0;
  let volume = 0;
  let entries = 0;
  let cashes = 0;

  const chart: ChartPoint[] = ordered.map((row) => {
    profit += row.profitUsd;
    investment += row.investmentUsd;
    volume += row.tournaments;
    entries += row.entries;
    cashes += row.cashes;
    return {
      date: row.date,
      label: shortDate(row.date),
      profit,
      roi: investment > 0 ? (profit / investment) * 100 : 0,
      volume,
      itm: volume > 0 ? (cashes / volume) * 100 : 0,
      abi: entries > 0 ? investment / entries : 0,
    };
  });

  const totals = ordered.reduce(
    (acc, row) => ({
      tournaments: acc.tournaments + row.tournaments,
      entries: acc.entries + row.entries,
      investmentUsd: acc.investmentUsd + row.investmentUsd,
      profitUsd: acc.profitUsd + row.profitUsd,
      cashes: acc.cashes + row.cashes,
      hours: acc.hours + (row.hours ?? 0),
      hasHours: acc.hasHours || row.hours !== null,
    }),
    {
      tournaments: 0,
      entries: 0,
      investmentUsd: 0,
      profitUsd: 0,
      cashes: 0,
      hours: 0,
      hasHours: false,
    },
  );

  return {
    roi: totals.investmentUsd > 0 ? (totals.profitUsd / totals.investmentUsd) * 100 : 0,
    profitUsd: totals.profitUsd,
    tournaments: totals.tournaments,
    itm: totals.tournaments > 0 ? (totals.cashes / totals.tournaments) * 100 : 0,
    abi: totals.entries > 0 ? totals.investmentUsd / totals.entries : 0,
    usdPerHour: totals.hasHours && totals.hours > 0 ? totals.profitUsd / totals.hours : null,
    chart,
  };
};
